import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Mail, Phone, MapPin, Instagram } from 'lucide-react';
import { settingsAPI } from '@/lib/api';

export const Footer = () => {
  const [company, setCompany] = useState({ name: '', email: '', phone: '', address: '', suburb: '' });
  const [instagram, setInstagram] = useState('');

  useEffect(() => {
    settingsAPI
      .getPublic()
      .then((r) => {
        if (r.data?.company) setCompany(r.data.company);
        if (r.data?.social?.instagram_url) setInstagram(r.data.social.instagram_url);
      })
      .catch(() => {});
  }, []);

  const email = company.email || 'hello@avzwholesale.co.za';
  const phone = company.phone || '';
  const suburb = company.suburb || '';

  return (
    <footer data-testid="footer" className="bg-secondary/20 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          <div>
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <img src="/logo.png" alt="AVZ Whole Foods" className="h-28 sm:h-48 w-auto logo-colored" />
            </div>
            <p className="font-body text-xs sm:text-sm text-foreground/60 leading-relaxed max-w-xs">
              Premium wholesale products from trusted suppliers. Reseller platform connecting you with quality vendors across South Africa.
            </p>
            <div className="flex items-center gap-2 mt-5 text-teal">
              <Leaf className="h-4 w-4" />
              <span className="font-body text-xs uppercase tracking-widest font-semibold">100% Natural</span>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-medium text-foreground mb-5">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/shop" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">Shop All Products</Link>
              <Link to="/about" data-testid="footer-about-link" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">About Us</Link>
              <Link to="/profile" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">My Account</Link>
              <Link to="/cart" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">Shopping Cart</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">Terms &amp; Conditions</Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">Privacy Policy</Link>
              <Link to="/faq" data-testid="footer-faq-link" className="font-body text-sm text-foreground/60 hover:text-teal transition-colors">FAQs</Link>
            </nav>
          </div>

          <div>
            <h4 className="font-heading text-lg font-medium text-foreground mb-5">Contact Us</h4>
            <div className="flex flex-col gap-3 font-body text-sm text-foreground/60" data-testid="footer-contact">
              {company.name && (
                <p className="text-foreground/80 font-medium" data-testid="footer-company-name">{company.name}</p>
              )}
              <a
                href={`mailto:${email}`}
                data-testid="footer-email"
                className="flex items-center gap-2 hover:text-teal transition-colors"
              >
                <Mail className="h-3.5 w-3.5 flex-shrink-0 text-teal" /> {email}
              </a>
              {phone && (
                <a
                  href={`tel:${phone.replace(/\s+/g, '')}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-2 hover:text-teal transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 flex-shrink-0 text-teal" /> {phone}
                </a>
              )}
              {suburb && (
                <p className="flex items-start gap-2" data-testid="footer-address">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-teal mt-0.5" />
                  <span className="whitespace-pre-line">{suburb}</span>
                </p>
              )}
            </div>

            {instagram && (
              <div className="mt-8" data-testid="footer-follow-section">
                <h4 className="font-heading text-lg font-medium text-foreground mb-5">Follow Us</h4>
                <a
                  href={instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="inline-flex items-center gap-2 font-body text-sm text-foreground/60 hover:text-teal transition-colors"
                >
                  <Instagram className="h-4 w-4 flex-shrink-0 text-teal" />
                  Follow us on Instagram
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-mint/30 mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-foreground/40">
            &copy; {new Date().getFullYear()} {company.name || 'AVZ Wholesale'}. All rights reserved.
          </p>
          <div className="flex items-center gap-1">
            <span className="font-body text-xs text-foreground/40">Made with</span>
            <span className="text-gold">♥</span>
            <span className="font-body text-xs text-foreground/40">in South Africa</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
